// features/sa-lab/hooks/useSaLabSession.js
//
// Stato della sessione di prova: creazione (POST /lab/sessioni), turni in
// streaming (POST /lab/sessioni/{id}/turno, SSE), avanzamento (STATO) e
// tempi per turno. Nessun dato persistito lato client.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth as useRealAuth } from '../../../context/AuthContext';
import { streamTurno } from '../api/streamTurno.js';

let contatore = 0;
const nuovoId = () => `m${Date.now()}_${(contatore += 1)}`;

export function useSaLabSession(kmClient) {
  const { token, logout } = useRealAuth();
  // Il token può cambiare durante la sessione: lo si legge sempre dal ref
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const [sessione, setSessione] = useState(null); // { session_id, profilo, modello }
  const [stato, setStato] = useState(null); // STATO del contratto
  const [messaggi, setMessaggi] = useState([]); // { id, ruolo, testo, tempi?, meta?, isError? }
  const [quickReplies, setQuickReplies] = useState([]);
  const [fase, setFase] = useState(null);
  const [chiusa, setChiusa] = useState(false);
  const [inStreaming, setInStreaming] = useState(false);
  const [attesaPrimoTesto, setAttesaPrimoTesto] = useState(false);
  const [avvioInCorso, setAvvioInCorso] = useState(false);
  const [errore, setErrore] = useState(null); // { messaggio, dettaglio? }

  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const eseguiTurno = useCallback(
    async (sessionId, body) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setErrore(null);
      setQuickReplies([]);
      setInStreaming(true);
      setAttesaPrimoTesto(true);

      const idRisposta = nuovoId();
      let creato = false;
      let metaTurno = null;
      let fineRicevuta = false;

      // Il messaggio dell'assistente nasce al primo "testo" (fino ad allora
      // si mostra l'indicatore di attesa) e poi si allunga a ogni delta.
      const accoda = (delta) => {
        if (!creato) {
          creato = true;
          setAttesaPrimoTesto(false);
          setMessaggi((prev) => [...prev, { id: idRisposta, ruolo: 'assistant', testo: delta, meta: metaTurno }]);
        } else {
          setMessaggi((prev) => prev.map((m) => (m.id === idRisposta ? { ...m, testo: m.testo + delta } : m)));
        }
      };

      const onEvento = (nome, data) => {
        if (controller.signal.aborted) return;
        if (nome === 'meta') {
          metaTurno = data;
          if (creato) setMessaggi((prev) => prev.map((m) => (m.id === idRisposta ? { ...m, meta: data } : m)));
        } else if (nome === 'testo') {
          if (typeof data?.delta === 'string' && data.delta) accoda(data.delta);
        } else if (nome === 'fine') {
          fineRicevuta = true;
          if (!creato) accoda(''); // fine senza testo: bolla vuota per appendere i tempi
          setMessaggi((prev) =>
            prev.map((m) => (m.id === idRisposta ? { ...m, tempi: data?.tempi || null, meta: m.meta || metaTurno } : m))
          );
          setQuickReplies(Array.isArray(data?.quick_replies) ? data.quick_replies : []);
          if (data?.fase !== undefined) setFase(data.fase);
          setChiusa(Boolean(data?.chiusa));
          if (data?.stato) setStato(data.stato);
        } else if (nome === 'errore') {
          setErrore({ messaggio: data?.messaggio || 'Errore dal server durante la risposta.' });
        }
      };

      try {
        await streamTurno({
          sessionId,
          body,
          getToken: () => tokenRef.current,
          onUnauthorized: logout,
          onEvento,
          signal: controller.signal,
        });
        if (!controller.signal.aborted && !fineRicevuta) {
          setErrore((prev) => prev || { messaggio: 'Lo stream si è chiuso senza l’evento di fine: risposta forse incompleta.' });
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setErrore({ messaggio: err?.detail || err?.message || 'Errore durante il turno.', dettaglio: err });
      } finally {
        if (abortRef.current === controller) {
          setInStreaming(false);
          setAttesaPrimoTesto(false);
        }
      }
    },
    [logout]
  );

  const avvia = useCallback(
    async ({ profilo, modello, nome, tempoTrascorso, progetto }) => {
      abortRef.current?.abort();
      setAvvioInCorso(true);
      setErrore(null);
      setMessaggi([]);
      setQuickReplies([]);
      setFase(null);
      setChiusa(false);
      setStato(null);
      setSessione(null);
      try {
        const body = { profilo, modello };
        if (nome?.trim()) body.nome = nome.trim();
        if (tempoTrascorso?.trim()) body.tempo_trascorso = tempoTrascorso.trim();
        if (progetto?.trim()) body.progetto = progetto.trim();
        const res = await kmClient.post('/lab/sessioni', body);
        setSessione({ session_id: res.session_id, profilo: res.profilo, modello: res.modello });
        if (res.stato) setStato(res.stato);
        setAvvioInCorso(false);
        // Apertura: primo messaggio dell'assistente
        await eseguiTurno(res.session_id, {});
      } catch (err) {
        setErrore({ messaggio: err?.detail || err?.message || 'Impossibile creare la sessione.', dettaglio: err });
      } finally {
        setAvvioInCorso(false);
      }
    },
    [kmClient, eseguiTurno]
  );

  const inviaTesto = useCallback(
    (testo) => {
      const t = (testo || '').trim();
      if (!t || !sessione || inStreaming) return;
      setMessaggi((prev) => [...prev, { id: nuovoId(), ruolo: 'user', testo: t }]);
      eseguiTurno(sessione.session_id, { testo: t });
    },
    [sessione, inStreaming, eseguiTurno]
  );

  const inviaScelta = useCallback(
    (qr) => {
      if (!qr || !sessione || inStreaming) return;
      setMessaggi((prev) => [...prev, { id: nuovoId(), ruolo: 'user', testo: qr.etichetta ?? String(qr.id) }]);
      eseguiTurno(sessione.session_id, { scelta: qr.id });
    },
    [sessione, inStreaming, eseguiTurno]
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSessione(null);
    setStato(null);
    setMessaggi([]);
    setQuickReplies([]);
    setFase(null);
    setChiusa(false);
    setInStreaming(false);
    setAttesaPrimoTesto(false);
    setErrore(null);
  }, []);

  // Riallinea messaggi e stato dal server (GET /lab/sessioni/{id}): utile se
  // uno stream si è interrotto. I tempi dei turni già visti si perdono.
  const ricarica = useCallback(async () => {
    if (!sessione) return;
    try {
      const res = await kmClient.get(`/lab/sessioni/${encodeURIComponent(sessione.session_id)}`);
      if (res?.stato) setStato(res.stato);
      if (Array.isArray(res?.messaggi)) {
        setMessaggi((prev) => {
          // Conserva i tempi per posizione, se il numero di messaggi coincide
          const tempiPerIndice = prev.map((m) => m.tempi);
          return res.messaggi.map((m, i) => ({
            id: nuovoId(),
            ruolo: m.ruolo,
            testo: m.testo,
            tempi: prev.length === res.messaggi.length ? tempiPerIndice[i] : undefined,
          }));
        });
      }
      setErrore(null);
    } catch (err) {
      setErrore({ messaggio: err?.detail || err?.message || 'Impossibile ricaricare la sessione.', dettaglio: err });
    }
  }, [kmClient, sessione]);

  return {
    sessione,
    stato,
    messaggi,
    quickReplies,
    fase,
    chiusa,
    inStreaming,
    attesaPrimoTesto,
    avvioInCorso,
    errore,
    chiudiErrore: () => setErrore(null),
    avvia,
    inviaTesto,
    inviaScelta,
    reset,
    ricarica,
  };
}
